import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trash2, RefreshCw, Search, ArrowLeft, AlertTriangle,
  RotateCcw, Calendar, User, Phone, Hash, FileText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Trash = () => {
  const [tab, setTab] = useState('customers');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ customers: 0, owners: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [purgeDialog, setPurgeDialog] = useState(null); // { id, name, invoiceCount } | 'bulk'
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/v2/trash/stats`, { headers });
      if (r.data?.success) setStats(r.data.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setSelected([]);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const url = `${API_URL}/api/v2/trash/${tab}?${params.toString()}`;
      const r = await axios.get(url, { headers });
      if (r.data?.success) setItems(r.data.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  // Reset to page 1 when tab/search changes
  useEffect(() => { setPage(1); setSelected([]); }, [tab, search, pageSize]);

  // Pagination
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  useEffect(() => { fetchStats(); fetchItems(); }, [fetchStats, fetchItems]);

  const handleRestore = async (id, name) => {
    try {
      await axios.post(`${API_URL}/api/v2/trash/${tab}/${id}/restore`, {}, { headers });
      toast.success(`✅ "${name}" berhasil dipulihkan`);
      fetchItems(); fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal restore');
    }
  };

  const handleBulkRestore = async () => {
    if (selected.length === 0) return;
    try {
      const body = tab === 'customers' ? { customer_ids: selected } : { owner_ids: selected };
      const r = await axios.post(`${API_URL}/api/v2/trash/${tab}/bulk-restore`, body, { headers });
      toast.success(`✅ ${r.data.restored} ${tab === 'customers' ? 'pelanggan' : 'owner'} dipulihkan`);
      fetchItems(); fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal bulk restore');
    }
  };

  const handlePermanentDelete = async () => {
    if (!purgeDialog) return;
    if (purgeConfirm !== 'HAPUS PERMANEN') {
      toast.error('Ketik "HAPUS PERMANEN" untuk konfirmasi');
      return;
    }
    try {
      if (purgeDialog === 'bulk') {
        // Bulk permanent purge: call bulk-delete with permanent=true
        const body = {
          customer_ids: selected,
          permanent: true,
          confirm_text: 'HAPUS PERMANEN',
        };
        await axios.post(`${API_URL}/api/v2/${tab}/bulk-delete`, body, { headers });
        toast.success(`✅ ${selected.length} ${tab} dihapus permanen`);
      } else {
        // Single permanent delete
        const url = `${API_URL}/api/v2/${tab}/${purgeDialog.id}?permanent=true&confirm_text=${encodeURIComponent('HAPUS PERMANEN')}`;
        await axios.delete(url, { headers });
        toast.success(`✅ "${purgeDialog.name}" dihapus permanen`);
      }
      setPurgeDialog(null);
      setPurgeConfirm('');
      fetchItems(); fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal hapus permanen');
    }
  };

  const toggleSelect = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };
  const toggleAll = () => {
    setSelected(s => s.length === items.length ? [] : items.map(i => i.id));
  };

  const fmtDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-6 space-y-4 max-w-7xl mx-auto" data-testid="trash-page">
      <div className="flex items-center gap-2">
        <Trash2 className="w-6 h-6 text-red-500" />
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Arsip Terhapus</h1>
        <Badge className="bg-amber-100 text-amber-700 ml-auto dark:bg-amber-900/40 dark:text-amber-300">Auto-purge 30 hari</Badge>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setTab('customers')}
          className={`p-3 rounded-lg border-2 transition-all text-left ${tab==='customers'
            ? 'border-red-400 bg-red-50 dark:bg-red-950/30'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}
          data-testid="tab-trash-customers"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400">Pelanggan</div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.customers}</div>
        </button>
        <button
          onClick={() => setTab('owners')}
          className={`p-3 rounded-lg border-2 transition-all text-left ${tab==='owners'
            ? 'border-red-400 bg-red-50 dark:bg-red-950/30'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}
          data-testid="tab-trash-owners"
        >
          <div className="text-xs text-slate-500 dark:text-slate-400">Owner</div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.owners}</div>
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama, no akun, PPPoE..."
            className="pl-9"
            data-testid="trash-search"
          />
        </div>
        <Button onClick={fetchItems} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-1" />Refresh
        </Button>
        {selected.length > 0 && (
          <>
            <Button onClick={handleBulkRestore} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
              <RotateCcw className="w-4 h-4 mr-1" />Pulihkan ({selected.length})
            </Button>
            <Button onClick={() => setPurgeDialog('bulk')} size="sm" variant="destructive">
              <Trash2 className="w-4 h-4 mr-1" />Hapus Permanen ({selected.length})
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-500 border-t-transparent" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 dark:text-slate-500">
          <Trash2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Tempat sampah kosong</p>
        </Card>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
            <input type="checkbox" checked={selected.length === items.length && items.length > 0} onChange={toggleAll} className="w-4 h-4 rounded" />
            <span className="text-xs text-slate-500 dark:text-slate-400">{items.length} item — dipilih: {selected.length}</span>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {paginatedItems.map(item => (
              <li key={item.id} className="px-3 py-3 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <input
                  type="checkbox"
                  checked={selected.includes(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  className="w-4 h-4 mt-1 rounded"
                  data-testid={`trash-select-${item.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">{item.name}</span>
                    {tab === 'customers' && item.account_number && (
                      <Badge variant="outline" className="text-[10px] font-mono">{item.account_number}</Badge>
                    )}
                    {item.invoice_count > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px] dark:bg-amber-900/40 dark:text-amber-300">
                        <FileText className="w-3 h-3 mr-0.5" />{item.invoice_count} invoice
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-1 dark:text-slate-400">
                    {item.pppoe_username && <span><Hash className="w-3 h-3 inline mr-0.5" />{item.pppoe_username}</span>}
                    {item.phone && <span><Phone className="w-3 h-3 inline mr-0.5" />{item.phone}</span>}
                    <span><Calendar className="w-3 h-3 inline mr-0.5" />Dihapus {fmtDate(item.deleted_at)}</span>
                    {item.deleted_by && <span><User className="w-3 h-3 inline mr-0.5" />oleh {item.deleted_by}</span>}
                  </div>
                  {item.days_until_purge !== undefined && (
                    <div className="mt-1">
                      <Badge className={`text-[10px] ${item.days_until_purge <= 7 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                        Auto-purge dalam {item.days_until_purge} hari
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
                    onClick={() => handleRestore(item.id, item.name)}
                    data-testid={`trash-restore-${item.id}`}
                    title="Pulihkan"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                    onClick={() => setPurgeDialog({ id: item.id, name: item.name, invoiceCount: item.invoice_count })}
                    data-testid={`trash-purge-${item.id}`}
                    title="Hapus Permanen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <Pagination
            page={safePage}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {/* Permanent purge confirmation */}
      <Dialog open={!!purgeDialog} onOpenChange={(open) => { if (!open) { setPurgeDialog(null); setPurgeConfirm(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Hapus Permanen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-900/30">
              <p className="text-sm text-red-700 font-semibold dark:text-red-300">⚠️ Operasi ini IRREVERSIBLE</p>
              {purgeDialog === 'bulk' ? (
                <p className="text-xs text-red-600 mt-1 dark:text-red-400">{selected.length} item akan dihapus permanen beserta semua data terkait (invoice, payment, tiket, RADIUS).</p>
              ) : purgeDialog && (
                <>
                  <p className="text-xs text-red-600 mt-1 dark:text-red-400"><b>{purgeDialog.name}</b> akan dihapus permanen.</p>
                  {purgeDialog.invoiceCount > 0 && (
                    <p className="text-xs text-red-600 mt-1 dark:text-red-400">Termasuk <b>{purgeDialog.invoiceCount} invoice</b> dan semua data transaksi terkait.</p>
                  )}
                </>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                Ketik <span className="font-mono bg-red-100 text-red-700 px-1 dark:bg-red-900/40 dark:text-red-300">HAPUS PERMANEN</span>:
              </label>
              <Input
                value={purgeConfirm}
                onChange={(e) => setPurgeConfirm(e.target.value)}
                placeholder="HAPUS PERMANEN"
                data-testid="purge-confirm-input"
                className="mt-1 font-mono border-red-300"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setPurgeDialog(null); setPurgeConfirm(''); }}>Batal</Button>
            <Button
              variant="destructive"
              onClick={handlePermanentDelete}
              disabled={purgeConfirm !== 'HAPUS PERMANEN'}
              data-testid="purge-confirm-btn"
              className="disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />Ya, Hapus Permanen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Trash;
